import { useEffect, useRef, useState } from 'react'
import { Heart, LoaderCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useLanguage } from '@/context/LanguageContext'

interface ItemAppreciationButtonProps {
  itemId: string
  initialCount: number
  initialLiked: boolean
  onChange?: (appreciation: { count: number; liked: boolean }) => void
}

interface AppreciationResult {
  item_id: string
  appreciation_count: number
  liked: boolean
}

const VISITOR_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function getAppreciationVisitorToken(): string {
  const cookieName = 'guide_appreciation_visitor'
  try {
    const existing = document.cookie.split(';').map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${cookieName}=`))?.slice(cookieName.length + 1)
    if (existing && VISITOR_TOKEN_PATTERN.test(existing)) {
      return existing
    }
  } catch {
    // cookies may be entirely blocked; fall through to localStorage
  }
  try {
    const stored = window.localStorage.getItem(cookieName)
    if (stored && VISITOR_TOKEN_PATTERN.test(stored)) {
      return stored
    }
  } catch {
    // localStorage unavailable too; fall through to a fresh token
  }
  const token = crypto.randomUUID()
  try {
    document.cookie = `${cookieName}=${token}; SameSite=Lax; Path=/; Max-Age=31536000${window.location.protocol === 'https:' ? '; Secure' : ''}`
    if (document.cookie.split(';').some((entry) => entry.trim() === `${cookieName}=${token}`)) {
      return token
    }
  } catch {
    // cookie write failed
  }
  try {
    window.localStorage.setItem(cookieName, token)
  } catch {
    // ephemeral token for this page load only
  }
  return token
}

export default function ItemAppreciationButton({ itemId, initialCount, initialLiked, onChange }: ItemAppreciationButtonProps) {
  const { language } = useLanguage()
  const [count, setCount] = useState(initialCount)
  const [liked, setLiked] = useState(initialLiked)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(false)
  const inFlight = useRef(false)
  const italian = language === 'it'
  const label = liked
    ? (italian ? 'Rimuovi apprezzamento' : 'Remove appreciation')
    : (italian ? 'Apprezza questo articolo' : 'Appreciate this item')

  useEffect(() => {
    setCount(initialCount)
    setLiked(initialLiked)
    setError(false)
  }, [itemId, initialCount, initialLiked])

  async function toggleAppreciation() {
    if (inFlight.current) return
    inFlight.current = true
    setPending(true)
    setError(false)
    try {
      const { data, error: rpcError } = await supabase.rpc('set_guide_item_appreciation', {
        _item_id: itemId,
        _visitor_token: getAppreciationVisitorToken(),
        _liked: !liked,
      })
      if (rpcError) throw rpcError
      const result = (data as unknown as AppreciationResult[] | null)?.find((row) => row.item_id === itemId)
      if (!result || !Number.isInteger(result.appreciation_count) || result.appreciation_count < 0 || typeof result.liked !== 'boolean') {
        throw new Error('Invalid appreciation response')
      }
      setCount(result.appreciation_count)
      setLiked(result.liked)
      onChange?.({ count: result.appreciation_count, liked: result.liked })
    } catch (cause) {
      console.error('Unable to save guide appreciation', cause)
      setError(true)
    } finally {
      inFlight.current = false
      setPending(false)
    }
  }

  return (
    <div className="absolute top-3 right-3 z-20 flex max-w-[calc(100%-1.5rem)] flex-col items-end gap-1" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        aria-label={`${label} · ${count}`}
        aria-pressed={liked}
        aria-busy={pending}
        disabled={pending}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void toggleAppreciation()
        }}
        className="inline-flex min-h-[32px] items-center justify-center gap-1 rounded-full border border-pink-500 bg-white/80 px-2 text-xs font-semibold text-pink-600 shadow-sm backdrop-blur-sm transition hover:bg-pink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
      >
        {pending ? <LoaderCircle aria-hidden="true" className="h-3 w-3 animate-spin text-pink-600" /> : (
          <Heart aria-hidden="true" className={`h-3 w-3 text-pink-600 ${liked ? 'fill-pink-500' : ''}`} />
        )}
        <span>{count}</span>
      </button>
      {error && (
        <span role="alert" className="max-w-40 rounded-lg bg-background/95 px-2 py-1 text-right text-xs text-foreground shadow-sm">
          {italian ? 'Non salvato. Riprova.' : 'Not saved. Try again.'}
        </span>
      )}
    </div>
  )
}
